import React from "react";
import LexCounselTriskelion from "./LexCounselTriskelion";

export type LexCounselMarkVariant = "icon" | "wordmark";

type LexCounselMarkProps = {
  variant?: LexCounselMarkVariant;
  /** Overall scale tier */
  size?: "sm" | "md";
  className?: string;
  /** Dark chip behind mark (matches brand lockup on any surface) */
  onDarkChip?: boolean;
  /** Show “AI” pill (launcher + marketing lockups) */
  showAiBadge?: boolean;
};

const SIZE = {
  sm: {
    icon: 22,
    gap: 8,
    chipPx: "px-2 py-1",
    chipRadius: "rounded-[10px]",
    ai: "text-[9px] px-1 py-px",
  },
  md: {
    icon: 28,
    gap: 10,
    chipPx: "px-2.5 py-1.5",
    chipRadius: "rounded-xl",
    ai: "text-[10px] px-1.5 py-0.5",
  },
} as const;

function LexCounselAiBadge({ size }: { size: "sm" | "md" }) {
  const dims = SIZE[size];
  return (
    <span
      className={`shrink-0 rounded-md bg-white/12 font-sans font-semibold uppercase leading-none tracking-wide text-white ${dims.ai}`}
    >
      AI
    </span>
  );
}

/**
 * Lex Counsel brand lockup — triskelion + "Lex" / "COUNSEL" typography.
 */
export default function LexCounselMark({
  variant = "wordmark",
  size = "md",
  className = "",
  onDarkChip = true,
  showAiBadge = false,
}: LexCounselMarkProps) {
  const dims = SIZE[size];
  const showWordmark = variant === "wordmark";

  const content = (
    <span
      className={`relative inline-flex items-center select-none font-sans antialiased subpixel-antialiased [text-rendering:geometricPrecision] ${
        showWordmark ? "gap-[var(--lex-mark-gap)]" : ""
      }`}
      style={{ "--lex-mark-gap": `${dims.gap}px` } as React.CSSProperties}
    >
      <span className="relative inline-flex shrink-0 transform-gpu items-center justify-center">
        <LexCounselTriskelion size={dims.icon} />
        {showAiBadge && !showWordmark && (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5">
            <LexCounselAiBadge size={size} />
          </span>
        )}
      </span>
      {showWordmark && (
        <>
          <span className="inline-flex items-baseline gap-[0.35em] whitespace-nowrap leading-none text-white">
            <span
              className={`font-semibold tracking-[-0.02em] ${
                size === "sm" ? "text-[13px]" : "text-[16px]"
              }`}
            >
              Lex
            </span>
            <span
              className={`font-normal uppercase ${
                size === "sm" ? "text-[9px] tracking-[0.28em]" : "text-[11px] tracking-[0.32em]"
              }`}
              style={{ marginRight: size === "sm" ? "-0.28em" : "-0.32em" }}
            >
              Counsel
            </span>
          </span>
          {showAiBadge && <LexCounselAiBadge size={size} />}
        </>
      )}
    </span>
  );

  if (!onDarkChip) {
    return <span className={`inline-flex items-center ${className}`}>{content}</span>;
  }

  return (
    <span
      className={`inline-flex items-center border-0 bg-[#141414] outline-none ${dims.chipRadius} ${dims.chipPx} ${className}`}
    >
      {content}
    </span>
  );
}
